import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      // Проверяем, есть ли токен для анкеты (не должен считаться как авторизация)
      const questionnaireToken = sessionStorage.getItem('questionnaire_token');
      if (questionnaireToken) {
        // Устанавливаем токен для запросов к анкете, но не логиним пользователя
        api.defaults.headers.common['Authorization'] = `Bearer ${questionnaireToken}`;
      }
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me/');
      setUser(response.data);
    } catch (error) {
      console.error('Ошибка получения пользователя:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login/', { username, password });
      const { access, user: userData } = response.data;
      if (!access || !userData?.is_verified) {
        return { success: false, error: 'Аккаунт не подтвержден' };
      }
      localStorage.setItem('token', access);
      setToken(access);
      setUser(userData);
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка входа',
      };
    }
  };

  const extractErrorMessage = (errorData) => {
    if (!errorData) return 'Ошибка регистрации';
    
    // Если это строка, возвращаем её
    if (typeof errorData === 'string') {
      return errorData;
    }
    
    // Если это объект с полями ошибок
    if (typeof errorData === 'object') {
      // Собираем все ошибки в массив
      const errors = [];
      
      // Проверяем конкретные поля в порядке приоритета
      if (errorData.email) {
        const emailError = Array.isArray(errorData.email) ? errorData.email[0] : errorData.email;
        errors.push(emailError);
      }
      if (errorData.phone) {
        const phoneError = Array.isArray(errorData.phone) ? errorData.phone[0] : errorData.phone;
        errors.push(phoneError);
      }
      if (errorData.username) {
        const usernameError = Array.isArray(errorData.username) ? errorData.username[0] : errorData.username;
        errors.push(usernameError);
      }
      if (errorData.password) {
        const passwordError = Array.isArray(errorData.password) ? errorData.password[0] : errorData.password;
        errors.push(passwordError);
      }
      
      // Проверяем non_field_errors
      if (errorData.non_field_errors) {
        const nonFieldError = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors;
        errors.push(nonFieldError);
      }
      
      // Если есть другие поля, добавляем их
      Object.keys(errorData).forEach(key => {
        if (!['email', 'phone', 'username', 'password', 'non_field_errors'].includes(key)) {
          const fieldError = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
          if (fieldError && typeof fieldError === 'string') {
            errors.push(`${key}: ${fieldError}`);
          }
        }
      });
      
      // Возвращаем первую ошибку или все ошибки через точку с запятой
      if (errors.length > 0) {
        return errors.length === 1 ? errors[0] : errors.join('. ');
      }
      
      // Если ошибок не найдено, но объект не пустой, пытаемся извлечь любую ошибку
      const firstKey = Object.keys(errorData)[0];
      if (firstKey) {
        const firstError = errorData[firstKey];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }
    }
    
    return 'Ошибка регистрации. Попробуйте еще раз.';
  };

  const register = async (userData, userType) => {
    try {
      const endpoint = userType === 'student' ? '/auth/register/student/' : '/auth/register/teacher/';
      const response = await api.post(endpoint, userData);
      
      if (userType === 'teacher') {
        // Для учителей не логиним автоматически, только сохраняем токен для анкеты
        const { questionnaire_token, email, message } = response.data;
        if (questionnaire_token) {
          // Сохраняем токен только для анкеты (не в основном localStorage)
          sessionStorage.setItem('questionnaire_token', questionnaire_token);
          // Устанавливаем токен для запросов к анкете
          api.defaults.headers.common['Authorization'] = `Bearer ${questionnaire_token}`;
        }
        return { 
          success: true, 
          requires_email_verification: false,
          is_verified: false,
          questionnaire_token: questionnaire_token,
        };
      } else {
        // Для студентов логинимся как обычно
        const { access, user: newUser, requires_email_verification } = response.data;
        if (access) {
          localStorage.setItem('token', access);
          setToken(access);
          setUser(newUser);
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        } else {
          logout();
        }

        return { 
          success: true, 
          requires_email_verification: requires_email_verification || false,
          is_verified: newUser?.is_verified || false,
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      
      // Если это 401, это странно для регистрации - возможно проблема с permissions
      if (error.response?.status === 401) {
        return {
          success: false,
          error: 'Ошибка доступа. Проверьте правильность введенных данных.',
        };
      }
      
      // Если это 400, это ошибка валидации - извлекаем сообщение
      if (error.response?.status === 400) {
        const errorMessage = extractErrorMessage(error.response?.data);
        return {
          success: false,
          error: errorMessage,
        };
      }
      
      // Для других ошибок пытаемся извлечь сообщение
      const errorMessage = extractErrorMessage(error.response?.data);
      return {
        success: false,
        error: errorMessage || `Ошибка регистрации (${error.response?.status || 'неизвестная ошибка'})`,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const updateUser = async () => {
    if (token) {
      await fetchCurrentUser();
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

